/* Share section — image-default presentation. The right column is a
   square ShareGrid image (rendered via shareImage.ts onto the
   PalinDragon monogram backdrop); the left column stacks the social-
   share buttons above the Copy to Clipboard row. The image regenerates
   automatically as guesses are committed and reveals are purchased.

   The text-grid presentation is gone from the visible UI, but the same
   emoji-string is still produced for ShareButtons (the deep-link text)
   and the Copy → Text path (clipboard muscle memory). */

import { useEffect, useState } from "preact/hooks";
import {
  guesses,
  HINT_COST,
  hintPurchasedAt,
  marksByGuess,
  puzzle,
  puzzleNumber,
  SPACES_COST,
  spacesPurchased,
  spacesPurchasedAt,
  status,
  statusLine,
} from "../game/state";
import { buildShareGrid, buildShareText } from "../game/share";
import { renderShareImageBlob } from "../game/shareImage";
import { ShareButtons } from "./ShareButtons";

type CopyState = "idle" | "text" | "image";

export function ShareGrid() {
  const allMarks = marksByGuess.value;
  const p = puzzle.value;
  // Don't render the share section until a puzzle is loaded — its
  // dimensions and emoji rows are computed off the solution.
  if (p == null) return null;
  // When the player hasn't bought Spaces, render the share grid in the
  // same bare-tile shape the board shows: letters-only, no spacing or
  // punctuation. `solutionNormalized` is letters-only lowercase; case
  // doesn't matter (every letter renders to an emoji), so it's a clean
  // proxy for "the bare mask".
  const renderText = spacesPurchased.value ? p.text : p.solutionNormalized;
  const text = buildShareText(
    allMarks,
    puzzleNumber.value,
    renderText,
    p.maxGuesses,
    hintPurchasedAt.value,
    HINT_COST,
    spacesPurchasedAt.value,
    SPACES_COST,
    status.value === "won",
  );
  const [copied, setCopied] = useState<CopyState>("idle");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Regenerate the displayed image whenever any input that drives the
  // grid changes. Each new blob is exposed via URL.createObjectURL; the
  // sibling effect (below) revokes the prior URL when state advances or
  // the component unmounts, so we don't leak object URLs over a session.
  useEffect(() => {
    let cancelled = false;
    const grid = buildShareGrid(
      allMarks,
      puzzleNumber.value,
      renderText,
      p.maxGuesses,
      hintPurchasedAt.value,
      HINT_COST,
      spacesPurchasedAt.value,
      SPACES_COST,
      status.value === "won",
    );
    renderShareImageBlob(grid)
      .then((blob) => {
        if (cancelled) return;
        setImageUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        /* canvas.toBlob is the only failure path and is rare in
           practice — leaving the previous image in place is the right
           default. */
      });
    return () => {
      cancelled = true;
    };
  }, [
    allMarks,
    puzzleNumber.value,
    renderText,
    p.maxGuesses,
    hintPurchasedAt.value,
    spacesPurchasedAt.value,
    status.value,
  ]);

  useEffect(() => {
    if (imageUrl == null) return;
    return () => URL.revokeObjectURL(imageUrl);
  }, [imageUrl]);

  // Compose the clipboard-text payload (text + dev-mode appends + the
  // canonical site URL). Centralized so both Copy paths stay in sync —
  // the image variant uses the same "what did the player share" data
  // model as the text variant; the only differences are the format and
  // the rendering target.
  const composeCopyText = (): string => {
    let copyText = text;
    let ribbonVisible = false;
    if (import.meta.env.DEV) {
      // The dev ribbon's visibility (Escape toggles it) decides whether
      // dev-only appends ride along on Copy. Hidden ribbon → dev mode is
      // out of the way and the clipboard payload matches what a player
      // would copy in production. Visible ribbon → the dev gets the
      // playing-state keyboard summary or the terminal-state solution
      // reveal, depending on game status.
      ribbonVisible =
        typeof document !== "undefined" &&
        document.body.classList.contains("body--has-dev-ribbon");
      if (ribbonVisible) {
        if (status.value === "playing") {
          const debug = (globalThis as typeof globalThis & {
            __PD_KEYBOARD_DEBUG__?: (
              guessRows: readonly (readonly (string | null)[])[],
              marks: readonly (readonly import("../game/scoring").Mark[])[],
              status: "playing" | "won" | "lost",
            ) => string | null;
          }).__PD_KEYBOARD_DEBUG__;
          if (debug != null) {
            const append = debug(
              guesses.value,
              marksByGuess.value,
              "playing",
            );
            if (append != null) copyText = `${text}\n\n${append}`;
          }
        } else {
          copyText = `${text}\n${p.text}\n`;
        }
      }
    }
    // Production and dev-ribbon-hidden: stamp the canonical site URL
    // as the last line so a shared / pasted Share Grid always carries
    // a clickable "where do I play this" pointer. With the dev ribbon
    // visible, the dev appends are the trailing context instead.
    if (!ribbonVisible) {
      copyText = `${copyText}\nhttps://palindragon.com`;
    }
    return copyText;
  };

  const flashCopied = (which: Exclude<CopyState, "idle">) => {
    setCopied(which);
    window.setTimeout(() => setCopied("idle"), 1500);
  };

  const onCopyText = async () => {
    try {
      await navigator.clipboard.writeText(composeCopyText());
      flashCopied("text");
    } catch {
      /* Clipboard API may be blocked (insecure context, denied permission).
         Silently leave the button label unchanged rather than misleading. */
    }
  };

  const onCopyImage = async () => {
    // Build the structured grid fresh (signals already evaluated above
    // would also work, but reading via the same args is symmetric with
    // buildShareText). The image carries the same content as the text
    // version — just rendered as colored squares on a themed card.
    const grid = buildShareGrid(
      allMarks,
      puzzleNumber.value,
      renderText,
      p.maxGuesses,
      hintPurchasedAt.value,
      HINT_COST,
      spacesPurchasedAt.value,
      SPACES_COST,
      status.value === "won",
    );
    try {
      // Safari requires the ClipboardItem value to be a Promise<Blob> when
      // the blob is generated asynchronously, so the user-gesture click
      // handler can return synchronously while the encode finishes.
      // Chrome/Firefox accept either a Blob or a Promise<Blob>.
      const blobPromise = renderShareImageBlob(grid);
      const item = new ClipboardItem({ "image/png": blobPromise });
      await navigator.clipboard.write([item]);
      flashCopied("image");
    } catch {
      /* Clipboard image-write may be unsupported (older Safari, Firefox
         without the dom.events.asyncClipboard.clipboardItem pref) or
         blocked. No fallback yet — the Text button still works. */
    }
  };

  return (
    <section class="sharegrid" aria-label="Share">
      <div class="sharegrid__image-wrap">
        {imageUrl != null && (
          <img
            class="sharegrid__image"
            src={imageUrl}
            alt="Your PalinDragon share grid"
          />
        )}
      </div>
      <div class="sharegrid__controls">
        <ShareButtons
          text={text}
          status={`PalinDragon #${puzzleNumber.value} — ${statusLine.value}`}
        />
        <div class="sharegrid__copyrow">
          <span class="sharegrid__copyrow-label">Copy to Clipboard:</span>
          <button
            type="button"
            class="sharegrid__button"
            onClick={onCopyText}
            aria-label="Copy share grid as text"
          >
            {copied === "text" ? "Copied!" : "Text"}
          </button>
          <button
            type="button"
            class="sharegrid__button"
            onClick={onCopyImage}
            aria-label="Copy share grid as image"
          >
            {copied === "image" ? "Copied!" : "Image"}
          </button>
        </div>
      </div>
    </section>
  );
}
